import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import {
  type ColumnFiltersState,
  type SortingState,
} from '@tanstack/react-table';
import { Button } from '@carbon/react';
import { ViewMode_1 as ViewModeIcon } from '@carbon/react/icons';
import { Link, PlainLink } from 'components/link/link';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import {
  createDivisionOrdersSearchAtom,
  type DivisionOrdersSearchParams,
} from './atoms';
import type { DivisionOrderResult } from './types';
import { DivisionOrdersFilters } from './division-orders-filters';
import { DivisionOrdersSummaryCards } from './summary-cards';
import { ExpandedLineItems } from './expanded-line-items';
import { DivisionOrdersFlatTable } from './division-orders-flat-table';
import { OperatorCellContent } from './operator-cell';
import { CollapsedList } from './collapsed-list';
import { DEFAULT_PARAMS, SORT_FIELD_MAP } from './utils';
import styles from './division-orders-content.module.scss';

type DORow = DivisionOrderResult & Record<string, unknown>;
type ViewMode = 'nested' | 'flat';

// Server pagination & filtering — `manualFiltering` is set by DataTable based
// on the presence of `scroll.loadData`, so client-side row filtering is a
// no-op. The filterFn assignments below only matter for column type
// validation in the filter components.
const columns: DataTableColumnDef<DORow>[] = [
  {
    accessorKey: 'name',
    header: 'Division Order',
    size: 160,
    cell: ({ row }) => <Link to={row.original.href}>{row.original.name}</Link>,
  },
  {
    accessorKey: 'source',
    header: 'DO Input Source',
    size: 150,
    meta: { type: 'enum' },
    cell: ({ row }) => {
      const { source, sourceDocument } = row.original;
      if (source === 'Provisioned' && sourceDocument?.href) {
        const title =
          sourceDocument.documentTitle ?? `Document #${sourceDocument.id}`;
        return (
          <Link to={sourceDocument.href} title={title}>
            Auto-Connected
          </Link>
        );
      }
      return source ?? 'Salesforce';
    },
  },
  {
    accessorKey: 'lineItemCount',
    header: 'Properties',
    size: 110,
    meta: { type: 'integer' },
  },
  {
    accessorKey: 'dateReceived',
    header: 'Date Received',
    size: 140,
    meta: { type: 'date' },
  },
  {
    id: 'operatorName',
    accessorFn: (row) => row.operator?.name ?? null,
    header: 'Operator',
    size: 200,
    cell: ({ row }) => <OperatorCellContent operator={row.original.operator} />,
  },
  {
    id: 'entityName',
    accessorFn: (row) =>
      row.lineItems
        .map((li) => li.entityName ?? li.crmAccountName)
        .filter(Boolean)
        .join(', '),
    header: 'Owner',
    size: 200,
    cell: ({ row }) => {
      type OwnerEntry = { key: string; name: string; entityId: number | null };
      const seen = new Set<string>();
      const owners: OwnerEntry[] = [];
      row.original.lineItems.forEach((li) => {
        if (li.entityId && li.entityName) {
          const key = `e:${li.entityId}`;
          if (!seen.has(key)) {
            seen.add(key);
            owners.push({ key, name: li.entityName, entityId: li.entityId });
          }
        } else if (li.crmAccountName) {
          const key = `a:${li.crmAccountId ?? li.crmAccountName}`;
          if (!seen.has(key)) {
            seen.add(key);
            owners.push({ key, name: li.crmAccountName, entityId: null });
          }
        }
      });

      if (owners.length === 0) return '--';
      const renderName = (o: OwnerEntry) => {
        if (o.entityId) {
          return <Link to={`/entities/${o.entityId}`}>{o.name}</Link>;
        }
        return o.name;
      };
      if (owners.length === 1) return renderName(owners[0]);
      return (
        <span>
          {renderName(owners[0])}
          <span>{` +${owners.length - 1}`}</span>
        </span>
      );
    },
  },
  {
    accessorKey: 'createdByName',
    header: 'Created By',
    size: 160,
    cell: ({ row }) => {
      if (row.original.source === 'Provisioned') return 'System';
      return row.original.createdByName ?? '--';
    },
  },
  {
    // Mirrors /afe_catalog "AFE File": combines the source-document file (a
    // URL link, no DB row) with any native SupportingFile rows. DO has no
    // native supporting files yet (item_id is integer, SF DO ids are
    // varchar) — supportingFiles is always [] today, so this cell typically
    // renders just the auto-connected document's PDF.
    id: 'doFileName',
    accessorFn: (row) => row.sourceDocument?.fileUrl ?? '',
    header: 'DO File',
    size: 160,
    cell: ({ row }) => {
      const source = row.original.sourceDocument;
      const combined: Array<{ key: string; label: string; href: string }> = [];

      if (source?.fileUrl) {
        combined.push({
          key: `src-${source.id}`,
          label: source.documentTitle ?? `Document #${source.id}`,
          href: source.fileUrl,
        });
      }

      (row.original.supportingFiles ?? []).forEach((f) => {
        if (!f.downloadUrl) return;
        combined.push({
          key: `sf-${f.id}`,
          label: f.fileName ?? `File #${f.id}`,
          href: f.downloadUrl,
        });
      });

      return (
        <CollapsedList
          items={combined}
          getLabel={(c) => c.label}
          renderItem={(c) => (
            <PlainLink href={c.href} target="_blank" rel="noopener noreferrer">
              {c.label}
            </PlainLink>
          )}
        />
      );
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Created At',
    size: 160,
    meta: { type: 'date' },
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated At',
    size: 160,
    meta: { type: 'date' },
  },
];

const initialTableState = {
  columnOrder: [
    '_expander',
    'name',
    'source',
    'lineItemCount',
    'dateReceived',
    'operatorName',
    'entityName',
    'createdByName',
    'createdAt',
    'updatedAt',
    'doFileName',
  ],
  sortColumn: 'createdAt',
  sortDirection: 'desc' as const,
};

// ─────────────────────────────────────
// Bridge: BE search params ↔ DataTable column-filter state
// ─────────────────────────────────────

const paramsToColumnFilters = (
  p: DivisionOrdersSearchParams
): ColumnFiltersState => {
  const filters: ColumnFiltersState = [];
  if (p.dateReceivedStart || p.dateReceivedEnd) {
    filters.push({
      id: 'dateReceived',
      value: [p.dateReceivedStart ?? null, p.dateReceivedEnd ?? null],
    });
  }
  if (p.createdAtStart || p.createdAtEnd) {
    filters.push({
      id: 'createdAt',
      value: [p.createdAtStart ?? null, p.createdAtEnd ?? null],
    });
  }
  if (p.updatedAtStart || p.updatedAtEnd) {
    filters.push({
      id: 'updatedAt',
      value: [p.updatedAtStart ?? null, p.updatedAtEnd ?? null],
    });
  }
  if (p.sources?.length) filters.push({ id: 'source', value: p.sources });
  if (p.operatorId) filters.push({ id: 'operatorName', value: p.operatorId });
  if (p.entityId) filters.push({ id: 'entityName', value: p.entityId });
  return filters;
};

const FILTER_PARAM_KEYS: Array<keyof DivisionOrdersSearchParams> = [
  'dateReceivedStart',
  'dateReceivedEnd',
  'createdAtStart',
  'createdAtEnd',
  'updatedAtStart',
  'updatedAtEnd',
  'sources',
  'operatorId',
  'entityId',
];

const columnFiltersToParamUpdates = (
  filters: ColumnFiltersState
): Partial<DivisionOrdersSearchParams> => {
  const updates: Partial<DivisionOrdersSearchParams> = {};
  FILTER_PARAM_KEYS.forEach((k) => {
    updates[k] = undefined;
  });
  filters.forEach((f) => {
    const v = f.value;
    if (f.id === 'dateReceived' && Array.isArray(v)) {
      updates.dateReceivedStart = (v[0] as string | null) ?? undefined;
      updates.dateReceivedEnd = (v[1] as string | null) ?? undefined;
    } else if (f.id === 'createdAt' && Array.isArray(v)) {
      updates.createdAtStart = (v[0] as string | null) ?? undefined;
      updates.createdAtEnd = (v[1] as string | null) ?? undefined;
    } else if (f.id === 'updatedAt' && Array.isArray(v)) {
      updates.updatedAtStart = (v[0] as string | null) ?? undefined;
      updates.updatedAtEnd = (v[1] as string | null) ?? undefined;
    } else if (f.id === 'source' && Array.isArray(v)) {
      updates.sources = v as DivisionOrdersSearchParams['sources'];
    } else if (f.id === 'operatorName' && typeof v === 'string') {
      updates.operatorId = v;
    } else if (f.id === 'entityName' && typeof v === 'number') {
      updates.entityId = v;
    }
  });
  return updates;
};

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

const DivisionOrdersContent: FC = () => {
  const { searchAtom } = useMemo(() => createDivisionOrdersSearchAtom(), []);
  const [searchResult, setSearchPayload] = useAtom(searchAtom);
  const [params, setParams] =
    useState<DivisionOrdersSearchParams>(DEFAULT_PARAMS);
  const [viewMode, setViewMode] = useState<ViewMode>('nested');

  useEffect(() => {
    setSearchPayload(() => params);
  }, [params, setSearchPayload]);

  const updateParams = useCallback(
    (updates: Partial<DivisionOrdersSearchParams>) => {
      setParams((current) => ({ ...current, page: 1, ...updates }));
    },
    []
  );

  const handleFilterChange = useCallback(
    (filters: ColumnFiltersState) => {
      updateParams(columnFiltersToParamUpdates(filters));
    },
    [updateParams]
  );

  const handleSortChange = useCallback(
    (sorting: SortingState) => {
      if (sorting.length === 0) {
        updateParams({ sortAttribute: 'createdAt', sortDirection: 'DESC' });
      } else {
        const sort = sorting[0];
        updateParams({
          sortAttribute: SORT_FIELD_MAP[sort.id] ?? sort.id,
          sortDirection: sort.desc ? 'DESC' : 'ASC',
        });
      }
    },
    [updateParams]
  );

  const handleGlobalSearchChange = useCallback(
    (search: string) => {
      const newText = search || undefined;
      if (params.text === newText) return;
      updateParams({ text: newText });
    },
    [params.text, updateParams]
  );

  const handleLoadData = useCallback(({ page, pageSize }: TableState) => {
    setParams((current) => ({ ...current, page, pageSize }));
  }, []);

  const results = searchResult.data?.results ?? [];
  const totalCount = searchResult.data?.totalCount ?? 0;

  // Hydrate DataTable's column filters from params only on mount (per instance).
  // Subsequent filter changes flow through handleFilterChange → params → BE.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialColumnFilters = useMemo(() => paramsToColumnFilters(params), []);

  const viewToggleButton = (
    <Button
      key="do-view-toggle"
      kind="ghost"
      size="lg"
      hasIconOnly
      renderIcon={ViewModeIcon}
      iconDescription={
        viewMode === 'nested'
          ? 'Switch to flat table'
          : 'Switch to nested table'
      }
      tooltipPosition="bottom"
      tooltipAlignment="end"
      onClick={() => setViewMode((m) => (m === 'nested' ? 'flat' : 'nested'))}
    />
  );

  return (
    <div className={styles.contentWrapper}>
      <DivisionOrdersSummaryCards
        statistics={searchResult.data?.aggregations}
      />

      <div className={styles.contentRow}>
        <div className={styles.tableArea}>
          {viewMode === 'flat' ? (
            <DivisionOrdersFlatTable
              parents={results as DivisionOrderResult[]}
              isLoading={searchResult.loading}
              error={searchResult.error ?? null}
              initialColumnFilters={initialColumnFilters}
              onFilterChange={handleFilterChange}
              renderFilterPanel={() => <DivisionOrdersFilters />}
              rightToolbarActions={[viewToggleButton]}
            />
          ) : (
            <DataTable<DORow>
              data={results as DORow[]}
              columns={columns}
              getRowId={(row) => String(row.id)}
              initialTableState={{
                ...initialTableState,
                columnFilters: initialColumnFilters,
              }}
              persistenceKey="division-orders-list-table-v2"
              config={{
                columnReordering: false,
                export: true,
              }}
              isLoading={searchResult.loading}
              error={searchResult.error ?? null}
              scroll={{
                mode: 'paginated',
                serverDriven: true,
                totalCount,
              }}
              onPaginationChange={handleLoadData}
              onSortChange={(s) => handleSortChange(s.sorting)}
              onFilterChange={(s) => handleFilterChange(s.columnFilters)}
              onGlobalSearchChange={(s) =>
                handleGlobalSearchChange(s.globalFilter)
              }
              searchPlaceholder="Search Division Orders"
              renderRowSubComponent={(row) => (
                <ExpandedLineItems row={row.original as DORow} />
              )}
              renderFilterPanel={() => <DivisionOrdersFilters />}
              rightToolbarActions={[viewToggleButton]}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DivisionOrdersContent;
